import { useState, useEffect, useCallback } from 'react';
import { getConnections } from '../utils/velvypalApi';
import { logger } from '../utils/logger';

/**
 * Hook for managing velvypal connections
 * @param {Object|null} user - Firebase user object
 * @returns {{
 *   connections: Array,
 *   newMessages: Array,
 *   conversations: Array,
 *   totalUnread: number,
 *   loading: boolean,
 *   error: string|null,
 *   refreshConnections: Function
 * }}
 */
export function useVelvypalConnections(user) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConnections = useCallback(async () => {
    if (!user) {
      setConnections([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getConnections(user);
      setConnections(data.connections || []);
    } catch (err) {
      logger.error('Failed to fetch connections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Refresh function for external use
  const refreshConnections = useCallback(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Filter into newMessages (incoming with no reply from current user) and conversations (ongoing exchanges)
  // New: incoming messages where I haven't replied yet (their_message_count > 0 && my_message_count === 0)
  const newMessages = connections.filter(
    c => c.direction === 'incoming' && (c.my_message_count === 0 || c.my_message_count === undefined)
  );

  // Conversations: all other connections (I've sent at least one message, or I initiated)
  const conversations = connections.filter(
    c => c.direction === 'outgoing' || (c.my_message_count && c.my_message_count > 0)
  );

  // Total unread count for badge
  const totalUnread = connections.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return {
    connections,
    newMessages,
    conversations,
    totalUnread,
    loading,
    error,
    refreshConnections,
  };
}
